// src/components/pages/message/timecapsule/Step2Recipients.js
import React, { useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { styled } from "@mui/material/styles";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  Box,
  Typography,
  CardMedia,
  Stack,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import IconNextArrow from "../../../../assets/icons/icon-next-arrow.svg";
import { ReactComponent as IconTrashRed } from "../../../../assets/icons/icon-trash-red.svg";
import ActionButton from "../../../atoms/ActionButton";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Styled phone input with country picker aligned vertically
const StyledPhoneInput = styled(PhoneInput)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  // Text input styling
  "& .PhoneInputInput": {
    flex: 1,
    height: "56px",
    borderRadius: "50px",
    paddingLeft: theme.spacing(1),
    fontSize: "1rem",
    border: `1px solid ${theme.palette.grey[400]}`,
    outline: "none",
  },
  // Country selector styling
  "& .PhoneInputCountrySelect": {
    display: "inline-flex",
    alignItems: "center",
    height: "56px",
    paddingLeft: theme.spacing(1),
    background: "transparent",
    border: 0,
    marginRight: theme.spacing(1),
  },
  // Flag icon alignment
  "& .PhoneInputCountryIcon": {
    verticalAlign: "middle",
  },
}));

const Step2Recipients = ({ wizardData, setWizardData, onNext, onBack }) => {
  // Manual phone input state
  const [phoneInput, setPhoneInput] = useState("");
  const [showPhoneField, setShowPhoneField] = useState(false);
  // Snackbar for errors
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const isMobileChrome =
    /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleShowPhoneField = () => {
    setShowPhoneField((prev) => !prev);
  };

  // Add a phone number
  const handleAddPhoneNumber = () => {
    // Validate via react-phone-number-input
    if (!phoneInput || !isValidPhoneNumber(phoneInput)) {
      setSnackbarMessage(
        "Please enter a valid phone number with a country code."
      );
      setSnackbarOpen(true);
      return;
    }

    setWizardData((prev) => ({
      ...prev,
      phoneNumbers: [...(prev.phoneNumbers || []), { phone: phoneInput }],
    }));

    setPhoneInput("");
  };

  // Remove a phone number
  const handleRemovePhoneNumber = (index) => {
    setWizardData((prev) => {
      const updated = [...(prev.phoneNumbers || [])];
      updated.splice(index, 1);
      return { ...prev, phoneNumbers: updated };
    });
  };

  // Contact Picker
  const handleContactPicker = async () => {
    if ("contacts" in navigator && "select" in navigator.contacts) {
      try {
        const props = ["name", "tel"];
        const opts = { multiple: true };
        const contacts = await navigator.contacts.select(props, opts);

        if (contacts && contacts.length > 0) {
          const newContacts = [];
          for (const c of contacts) {
            if (Array.isArray(c.tel)) {
              c.tel.forEach((number) => {
                const phoneNumber = parsePhoneNumberFromString(number, "US");
                if (phoneNumber && phoneNumber.isValid()) {
                  newContacts.push({
                    name: c.name ? c.name[0] : undefined,
                    phone: phoneNumber.format("E.164"),
                  });
                }
              });
            }
          }

          setWizardData((prev) => ({
            ...prev,
            phoneNumbers: [...(prev.phoneNumbers || []), ...newContacts],
          }));
        }
      } catch (err) {
        console.error("Contact Picker error:", err);
      }
    } else {
      setSnackbarMessage(
        "Contact Picker not supported on this device/browser."
      );
      setSnackbarOpen(true);
    }
  };

  // Next step validation
  const handleNextStep = () => {
    const hasPhoneNumbers =
      wizardData.phoneNumbers && wizardData.phoneNumbers.length > 0;
    const hasRecipients =
      wizardData.recipients && wizardData.recipients.length > 0;

    if (!hasPhoneNumbers && !hasRecipients) {
      setSnackbarMessage("Please add at least one recipient.");
      setSnackbarOpen(true);
      return;
    }
    onNext();
  };

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Step 2: Select Recipients
      </Typography>
      <Typography variant="body1" gutterBottom>
        Decide who can see this message and set a time for it to be revealed.
      </Typography>

      {/* Contact picker & manual entry toggles */}
      <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
        {isMobileChrome && (
          <ActionButton
            text="My Contacts"
            onClick={handleContactPicker}
            sx={(theme) => ({
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              border: `1px solid ${theme.colors.blue}`,
              borderRadius: "50px",
              padding: "6px 10px",
              fontSize: "1rem",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            })}
          />
        )}
        <ActionButton
          text="Enter Phone #"
          onClick={handleShowPhoneField}
          sx={(theme) => ({
            backgroundColor: theme.colors.white,
            color: theme.colors.blue,
            border: `1px solid ${theme.colors.blue}`,
            borderRadius: "50px",
            padding: "6px 10px",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
      </Box>

      {/* Manual phone input + add button */}
      {showPhoneField && (
        <Box
          sx={{ display: "flex", gap: 1, mt: 2, mb: 3, alignItems: "center" }}
        >
          <StyledPhoneInput
            defaultCountry="US"
            international
            countryCallingCodeEditable={false}
            placeholder="Enter phone number"
            value={phoneInput}
            onChange={setPhoneInput}
            type="tel"
          />
          <IconButton
            onClick={handleAddPhoneNumber}
            sx={(theme) => ({
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              "&:hover": { backgroundColor: theme.colors.darkBlue },
            })}
          >
            <AddIcon />
          </IconButton>
        </Box>
      )}

      {/* Selected phone numbers list */}
      {wizardData.phoneNumbers && wizardData.phoneNumbers.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2, mt: 2 }}>
          {wizardData.phoneNumbers.map((item, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography
                sx={(theme) => ({
                  color: theme.colors.blue,
                  fontWeight: 700,
                })}
              >
                {item.name
                  ? `${item.name} (${item.phone})`
                  : item.phone}
              </Typography>
              <IconButton
                sx={{ width: 35, height: 35 }}
                onClick={() => handleRemovePhoneNumber(index)}
              >
                <IconTrashRed />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* Media preview */}
      {wizardData.mediaFile && (
        <Box sx={{ mb: 2, mt: 3 }}>
          {wizardData.mediaFile.type.startsWith("image/") ? (
            <CardMedia
              component="img"
              image={wizardData.mediaFile.url}
              alt="preview"
              sx={{ width: "100%", borderRadius: "20px" }}
            />
          ) : wizardData.mediaFile.type.startsWith("video/") ? (
            <CardMedia
              component="video"
              src={wizardData.mediaFile.url}
              controls
              sx={{ maxHeight: 200 }}
            />
          ) : null}
        </Box>
      )}

      {/* Reveal date picker */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Drop & Reveal!:
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
        (Optional) Decide when this message will be available for others.
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select reveal date"
          value={wizardData.revealDate || null}
          onChange={(newValue) =>
            setWizardData((prev) => ({ ...prev, revealDate: newValue }))
          }
          slotProps={{
            textField: {
              fullWidth: true,
              sx: {
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "50px",
                  },
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      {/* Navigation buttons */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 10,
        }}
      >
        <ActionButton
          text="< Back"
          onClick={onBack}
          sx={(theme) => ({
            backgroundColor: theme.colors.white,
            color: "black",
            textDecoration: "underline",
            borderRadius: "50px",
            padding: "14px 0",
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
        <ActionButton
          icon={IconNextArrow}
          iconWidth={15}
          iconHeight={15}
          text="Next Step"
          iconPosition="right"
          onClick={handleNextStep}
          sx={(theme) => ({
            backgroundColor: theme.colors.blue,
            color: theme.colors.white,
            borderRadius: "50px",
            padding: "14px 30px",
            fontWeight: "bold",
            fontSize: "1rem",
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
      </Box>

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Step2Recipients;
